"use client"
import React, { useState, useEffect } from "react"
import Button from "../../../components/common/Button"
import Modal from "../../../components/common/Modal"
import { Edit, Plus, Trash2, HelpCircle, MessageSquare, X } from "lucide-react"

interface FAQStepProps {
  data: any
  onChange: (data: any) => void
  formRef?: React.RefObject<HTMLFormElement | null>
  onBack: () => void
  onNext?: () => void
  onPublish?: () => void
  onSaveDraft?: () => void
  loading?: boolean
}

interface FAQ {
  question: string;
  answer: string;
}

const FAQStep: React.FC<FAQStepProps> = ({ 
  data, 
  onChange, 
  formRef,
  onBack,
  onNext,
  onPublish,
  onSaveDraft,
  loading = false
}) => {
  const [faqs, setFaqs] = useState<FAQ[]>(data.faqs && data.faqs.length > 0 ? data.faqs : [])
  const [currentFaq, setCurrentFaq] = useState<FAQ | null>(null)
  const [editingFaqIndex, setEditingFaqIndex] = useState<number | null>(null)
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Update faqs when data changes
  useEffect(() => {
    if (data.faqs && data.faqs.length > 0) {
      setFaqs(data.faqs);
    }
  }, [data.faqs]);

  const handleAddFaq = () => {
    setCurrentFaq({ question: '', answer: '' })
    setEditingFaqIndex(null)
    setIsFaqModalOpen(true)
  }

  const handleEditFaq = (index: number) => {
    setCurrentFaq({ ...faqs[index] })
    setEditingFaqIndex(index)
    setIsFaqModalOpen(true)
  }

  const handleDeleteFaq = (index: number) => {
    const newFaqs = [...faqs]
    newFaqs.splice(index, 1)
    setFaqs(newFaqs)
    onChange({ ...data, faqs: newFaqs })
  }

  const handleSaveFaq = () => {
    if (!currentFaq?.question || !currentFaq?.answer) {
      setError("Both question and answer are required")
      return
    }
    
    // Ensure we're not sending any extra fields that might cause API issues
    const cleanFaq = {
      question: currentFaq.question,
      answer: currentFaq.answer
    };
    
    const newFaqs = [...faqs]
    if (editingFaqIndex !== null) {
      // Update existing FAQ
      newFaqs[editingFaqIndex] = cleanFaq
    } else {
      // Add new FAQ
      newFaqs.push(cleanFaq)
    }
    
    setFaqs(newFaqs)
    onChange({ ...data, faqs: newFaqs })
    setIsFaqModalOpen(false)
    setCurrentFaq(null)
    setEditingFaqIndex(null)
    setError(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-center mb-2">
          <div className="bg-indigo-100 rounded-full p-1.5 mr-2">
            <HelpCircle className="text-indigo-600 w-5 h-5" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Frequently Asked Questions</h2>
        </div>
        <p className="text-gray-600 text-sm">Help your customers by providing answers to common queries about your package.</p>
      </div>
      
      {/* FAQ Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-xs">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="bg-green-100 rounded-full p-1.5 mr-2">
              <HelpCircle className="text-green-600 w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Package FAQs</h3>
              <p className="text-xs text-gray-600">Provide answers to help customers understand your package</p>
            </div>
          </div>
          <Button
            onClick={handleAddFaq}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white flex items-center shadow-sm hover:shadow-md transition-all duration-200 text-xs px-3 py-1.5"
          >
            <Plus className="mr-1 w-3 h-3" /> Add FAQ
          </Button>
        </div>
        
        {faqs.length === 0 ? (
          <div className="text-center py-8">
            <div className="bg-gray-100 rounded-full p-3 w-12 h-12 mx-auto mb-3 flex items-center justify-center">
              <MessageSquare className="text-gray-400 w-6 h-6" />
            </div>
            <p className="text-gray-500 font-medium mb-1 text-sm">No FAQs added yet</p>
            <p className="text-gray-400 text-xs">Add frequently asked questions to help your customers</p>
          </div>
        ) : (
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-3 bg-gradient-to-br from-gray-50 to-white hover:shadow-xs transition-shadow duration-200">
                <div className="flex justify-between items-start">
                  <div className="flex-1 pr-3">
                    <div className="flex items-start mb-1.5">
                      <div className="bg-blue-100 rounded-full p-0.5 mr-1.5 mt-0.5">
                        <HelpCircle className="w-2.5 h-2.5 text-blue-600" />
                      </div>
                      <h4 className="font-semibold text-gray-900 text-sm leading-tight">{faq.question}</h4>
                    </div>
                    <p className="text-gray-600 ml-4 text-xs leading-relaxed">{faq.answer}</p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleEditFaq(index)}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded-md hover:bg-blue-50 transition-colors duration-200"
                      title="Edit FAQ"
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteFaq(index)}
                      className="text-red-600 hover:text-red-800 p-1 rounded-md hover:bg-red-50 transition-colors duration-200"
                      title="Delete FAQ"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center">
            <div className="bg-red-100 rounded-full p-1 mr-1.5">
              <X className="text-red-600 w-3 h-3" />
            </div>
            <p className="text-red-700 font-medium text-sm">{error}</p>
          </div>
        </div>
      )}
      
      {/* FAQ Modal */}
      <Modal
        isOpen={isFaqModalOpen}
        onClose={() => {
          setIsFaqModalOpen(false)
          setCurrentFaq(null)
          setEditingFaqIndex(null)
          setError(null)
        }}
        title={editingFaqIndex !== null ? "Edit FAQ" : "Add FAQ"}
        className="w-full max-w-lg mx-auto my-8"
      >        <div className="space-y-4 p-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-2">
              <div className="flex items-center">
                <div className="bg-red-100 rounded-full p-1 mr-1.5">
                  <X className="text-red-600 w-2.5 h-2.5" />
                </div>
                <p className="text-red-700 text-xs font-medium">{error}</p>
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Question</label>
            <input
              type="text"
              value={currentFaq?.question || ''}
              onChange={(e) => setCurrentFaq(prev => prev ? ({ ...prev, question: e.target.value }) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-sm"
              placeholder="Enter your question here..."
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Answer</label>
            <textarea
              value={currentFaq?.answer || ''}
              onChange={(e) => setCurrentFaq(prev => prev ? ({ ...prev, answer: e.target.value }) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 min-h-[100px] transition-colors resize-none text-sm"
              placeholder="Provide a detailed answer..."
            />
          </div>
          <div className="flex justify-end space-x-2 pt-3 border-t border-gray-100">
            <Button
              variant="secondary"
              onClick={() => {
                setIsFaqModalOpen(false)
                setCurrentFaq(null)
                setEditingFaqIndex(null)
                setError(null)
              }}
              className="px-4 py-1.5 border border-gray-300 hover:bg-gray-50 transition-colors text-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveFaq}
              className="px-4 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white transition-all duration-200 text-sm"
            >
              Save FAQ
            </Button>
          </div>
        </div>
      </Modal>

      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row justify-between items-center pt-6 border-t border-gray-200 space-y-3 sm:space-y-0">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-purple-500 hover:text-purple-600 transition-all duration-200 font-medium flex items-center space-x-1.5 text-sm"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>Back</span>
        </button>
        <div className="flex space-x-2">
          {onSaveDraft && (
            <button
              type="button"
              onClick={onSaveDraft}
              disabled={loading}
              className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:border-purple-500 hover:text-purple-600 transition-all duration-200 font-medium flex items-center space-x-1.5 disabled:opacity-50 text-sm"
            >
              <span>Save as Draft</span>
            </button>
          )}
          <button
            type="button"
            onClick={onPublish || onNext}
            disabled={loading}
            className="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-md hover:shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-1.5 text-sm"
          >
            {loading ? (
              <>
                <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 718-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{onPublish ? 'Updating...' : 'Publishing...'}</span>
              </>
            ) : (
              <>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{onPublish ? 'Update Package' : 'Publish Package'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default FAQStep